/**
 * FormationReferenceVisual - Visual component for formation_reference blocks
 *
 * Uses CanvasRenderer with pre-resolved data to render formations inline.
 * Parent containers (SlideVisual, SectionVisual) embed resolved formation
 * in properties.__resolved_formation before passing to BlockRenderer.
 */

import { ReferenceWrapper, type ReferenceDisplayOptions } from './ReferenceWrapper'
import { CanvasRenderer } from '@/components/canvas-renderer'
import { THUMBNAIL_VIEW_BOX } from '@/constants'
import type { BlockVisualProps } from '../../Types'
import type { FormationReferenceBlock, FormationBlock, ResolvedBlock, PlayerBlock } from '@/types/blocks'

export function FormationReferenceVisual({ block }: BlockVisualProps) {
	if (block.type !== 'formation_reference') return null

	const refBlock = block as FormationReferenceBlock
	const resolvedFormation = (refBlock.properties as Record<string, unknown>).__resolved_formation as FormationBlock | null
	const displayOptions = ((refBlock.properties as Record<string, unknown>).__display_options ?? {}) as ReferenceDisplayOptions

	if (!resolvedFormation) {
		return (
			<div className="formation-reference-empty">
				<p style={{ color: 'var(--muted-foreground)' }}>Formation not found</p>
			</div>
		)
	}

	// Get aspect ratio from field config if available
	const formationProps = resolvedFormation.properties as { fieldConfig?: { width: number; length: number } } | undefined
	const aspectRatio = getFieldAspectRatio(formationProps?.fieldConfig)

	// Build ResolvedBlock from formation data for CanvasRenderer
	const players = ((resolvedFormation.properties as Record<string, unknown>).__resolved_players ?? []) as PlayerBlock[]
	const resolvedBlock: ResolvedBlock = {
		block: resolvedFormation,
		players,
		drawings: [],
		annotations: [],
		formationBlock: null,
		conceptReferences: [],
	}

	return (
		<ReferenceWrapper displayOptions={displayOptions} aspectRatio={aspectRatio}>
			<CanvasRenderer
				resolvedBlock={resolvedBlock}
				mode="static"
				viewBox={THUMBNAIL_VIEW_BOX}
			/>
		</ReferenceWrapper>
	)
}

function getFieldAspectRatio(fieldConfig?: { width: number; length: number }): number {
	if (!fieldConfig) return 16 / 9
	return fieldConfig.width / fieldConfig.length
}
